import React from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import EmailIcon from "@mui/icons-material/Email";
import { makeStyles } from "tss-react/mui";

const useStyles = makeStyles()((theme) => ({
  dialogContent: {
    paddingTop: theme.spacing(2),
  },
  requirementBox: {
    backgroundColor: theme.palette.action.hover,
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    marginBottom: theme.spacing(2),
  },
  emailButton: {
    marginTop: theme.spacing(2),
  },
  section: {
    marginBottom: theme.spacing(3),
  },
  benefitIcon: {
    color: theme.palette.success.main,
  },
}));

function BlogSubmissionModal({ open, onClose }) {
  const { classes } = useStyles();

  const handleEmailClick = () => {
    const subject = encodeURIComponent(
      "Founding Partner Blog Post/Case Study Submission"
    );
    const body = encodeURIComponent(
      `Hi Oliver,\n\nI would like to submit a blog post/case study for publication on ContentGenerator.io.\n\n[Please attach your blog post or case study document]\n\nType of submission: [Blog Post / Case Study]\nWord count: [Your word count]\n\nBest regards,\n[Your name]`
    );
    window.location.href = `mailto:Oliver@ContentGenerator.io?subject=${subject}&body=${body}`;
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Typography variant="h3">Blog Post & Case Study Submissions</Typography>
      </DialogTitle>
      <DialogContent className={classes.dialogContent}>
        <div className={classes.section}>
          <Typography variant="h4" gutterBottom>
            How It Works
          </Typography>
          <Typography variant="body1" paragraph>
            As a Founding Partner, you have the opportunity to contribute blog
            posts and case studies to the ContentGenerator.io blog. This is a
            great way to share your expertise, showcase your success stories,
            and gain exposure to our audience.
          </Typography>
        </div>

        <Divider />

        <div className={classes.section} style={{ marginTop: 24 }}>
          <Typography variant="h4" gutterBottom>
            Benefits
          </Typography>
          <List>
            <ListItem>
              <ListItemIcon>
                <CheckCircleIcon className={classes.benefitIcon} />
              </ListItemIcon>
              <ListItemText primary="Get published on ContentGenerator.io's blog" />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckCircleIcon className={classes.benefitIcon} />
              </ListItemIcon>
              <ListItemText primary="Link back to your website" />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckCircleIcon className={classes.benefitIcon} />
              </ListItemIcon>
              <ListItemText primary="Share your expertise and success stories" />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckCircleIcon className={classes.benefitIcon} />
              </ListItemIcon>
              <ListItemText primary="Gain exposure to our audience" />
            </ListItem>
          </List>
        </div>

        <Divider />

        <div className={classes.section} style={{ marginTop: 24 }}>
          <Typography variant="h4" gutterBottom>
            Submission Requirements
          </Typography>
          <div className={classes.requirementBox}>
            <Typography variant="body1" paragraph>
              <strong>Content Length:</strong> Minimum 1,000 words
            </Typography>
            <Typography variant="body1" paragraph>
              <strong>Format:</strong> Submit as a Google Doc
            </Typography>
            <Typography variant="body1" paragraph>
              <strong>Links:</strong> You may include links back to your website
              or relevant resources
            </Typography>
            <Typography variant="body1" paragraph>
              <strong>Images:</strong> Include any relevant images, screenshots,
              or graphics (properly licensed)
            </Typography>
            <Typography variant="body1" paragraph>
              <strong>Author Bio:</strong> Include a short bio (2-3 sentences)
              with your submission
            </Typography>
            <Typography variant="body1" paragraph style={{ marginBottom: 0 }}>
              <strong>Rights:</strong> We reserve the right to make edits for
              SEO purposes or to bring the content in line with our blog format
            </Typography>
          </div>
        </div>

        <Divider />

        <div className={classes.section} style={{ marginTop: 24 }}>
          <Typography variant="h4" gutterBottom>
            Review Process
          </Typography>
          <Typography variant="body1" paragraph>
            After you submit your blog post or case study, we'll review it to
            ensure it meets our quality standards and aligns with our blog's
            content strategy. We typically respond within 5-7 business days with
            feedback or approval for publication.
          </Typography>
        </div>

        <Divider />

        <div className={classes.section} style={{ marginTop: 24 }}>
          <Typography variant="h4" gutterBottom>
            Ready to Submit?
          </Typography>
          <Typography variant="body1" paragraph>
            Click the button below to compose an email with your submission.
            Make sure to attach your completed blog post or case study document.
          </Typography>
          <Button
            variant="contained"
            color="primary"
            size="large"
            startIcon={<EmailIcon />}
            onClick={handleEmailClick}
            className={classes.emailButton}
            fullWidth
          >
            Submit via Email to Oliver@ContentGenerator.io
          </Button>
        </div>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}

BlogSubmissionModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default BlogSubmissionModal;
